// Talking to the assistant: the browser's own speech recognition turns what
// the user says into their message. Nothing new leaves the device beyond what
// the browser itself does for dictation, and there's no extra cost. Replies
// stay as text (the browser's voices sounded robotic). Where the browser has
// no speech recognition (some in-app web views), `supported` is false and
// the mic isn't shown.

import { useCallback, useEffect, useRef, useState } from 'react';

const Recognition = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : undefined;

export const voiceSupported = () => !!Recognition;

/**
 * Listen once: `onText(text)` as the words come in, `onDone(text)` when the
 * user stops talking (text may be empty if nothing was heard).
 */
export const useVoice = () => {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);

  const stopListening = useCallback(() => {
    const r = recognitionRef.current;
    recognitionRef.current = null;
    if (r) {
      r.onend = null;
      r.onresult = null;
      r.onerror = null;
      try { r.abort(); } catch { /* already stopped */ }
    }
    setListening(false);
  }, []);

  const listen = useCallback(({ onText, onDone }) => {
    if (!Recognition) return;
    stopListening();
    const r = new Recognition();
    r.lang = navigator.language || 'en-US';
    r.interimResults = true;
    r.continuous = false;
    let heard = '';
    r.onresult = (e) => {
      heard = Array.from(e.results).map((res) => res[0].transcript).join('').trim();
      onText(heard);
    };
    r.onerror = (e) => {
      // "no-speech" and "aborted" just end the turn; anything else is worth saying
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setError('Microphone access is off for this app.');
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setError("Couldn't hear that. Try again.");
    };
    r.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      onDone(heard);
    };
    recognitionRef.current = r;
    setError(null);
    setListening(true);
    try {
      r.start();
    } catch {
      recognitionRef.current = null;
      setListening(false);
    }
  }, [stopListening]);

  useEffect(() => () => stopListening(), [stopListening]);

  return { supported: !!Recognition, listening, error, listen, stopListening };
};
